import {
  Box,
  DropdownButtonContext,
  FlexBox,
  Icon,
  InputRichText,
  Text,
} from '@dixa/lib-shared-habla';
import { typography } from '@dixa/lib-shared-tokens';
import { Channel } from 'lib-shared-types-channels';
import { memo, useContext } from 'react';
import { useMount } from 'react-use';

import { aiApi, ReadAiWritingImprovementArg } from 'apis/ai';
import { AiLoadingState } from 'components/aiLoadingState';
import { useEditorContext } from 'utils/useEditorContext';

interface EditorCopilotDropdownMatchToStyleGuideProps {
  channel: Channel;
  lastMessage: string;
}

/**
 * Component that represents the "match to style guide" action in the copilot
 * dropdown.
 */
export const EditorCopilotDropdownMatchToStyleGuide = memo(
  function EditorCopilotDropdownMatchToStyleGuide({
    channel,
    lastMessage,
  }: EditorCopilotDropdownMatchToStyleGuideProps) {
    const { replace, getSelectedText } = useEditorContext();
    const dropdownContext = useContext(DropdownButtonContext);
    const { content } = useContext(InputRichText.Context);
    const [improveReply] = aiApi.useCreateAiWritingImprovementMutation();
    const text = getSelectedText();

    const improveReplyContext: ReadAiWritingImprovementArg['contexts'] = {
      features: 'impr_reply',
      channels: channel,
    };

    const performMatchToStyleGuide = async () => {
      dropdownContext.closeDropdown();
      content.setUiBlocker(
        <Box style={{ position: 'absolute', bottom: 16 }}>
          <AiLoadingState label="Matching to writing style guide" />
        </Box>,
      );

      try {
        const response = await improveReply({
          lastMessage,
          text,
          promptName: 'repeat-input',
          contexts: improveReplyContext,
        }).unwrap();
        replace(response.text);
        content.setUiBlocker(null);
      } catch {
        content.setUiBlocker(
          <FlexBox
            style={{
              position: 'absolute',
              height: '100%',
              width: '100%',
              paddingBottom: '30px',
            }}
            alignItems="flex-end"
            justifyContent="center"
            gap="200"
            onClick={() => {
              content.setUiBlocker(null);
            }}
          >
            <Text.Paragraph
              variant="1"
              color={typography.color.error}
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <Icon
                name="exclamation"
                color={typography.color.error}
                style={{ marginRight: '8px' }}
              />
              Failed to apply Writing Style Guide
            </Text.Paragraph>
          </FlexBox>,
        );
      }
    };

    useMount(() => {
      if (text && improveReplyContext) {
        performMatchToStyleGuide();
      }
    });

    return null;
  },
);
